<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout" class="w-100 h-100">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />
    <validation-modal />
  </div>
</template>

<script>
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";
import ValidationModal from "@core/components/base-modal/ValidationModal.vue";

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch, getCurrentInstance } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import { useWindowSize, useCssVar } from "@vueuse/core";
import store from "@/store";
import themeModel from "@/models/theme/theme";
import menuModel from "@/models/menu/menu";
import { cssDefault } from "@/configs/css-variables/css-default";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
  components: {
    // Layouts
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
    ValidationModal,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
  },

  async beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");

    //set css variables
    const { fetchGetThemeDetail } = themeModel();
    fetchGetThemeDetail.payload.brand_ref = await this.$store.dispatch(
      "brand/fetchBrandRef"
    );
    await this.$store
      .dispatch("api/apiRequest", fetchGetThemeDetail)
      .then((response) => {
        this.$store.commit(
          "appConfig/UPDATE_CSS_VARIABLES",
          response.data.theme_fields
        );
        this.$store.commit(
          "appConfig/UPDATE_NAVIGATION_CONFIG",
          response.data.dynamic_fields
        );
        this.$store.commit(
          "brand/SET_BRAND_NAME",
          response.data.theme_name_th,
          { root: true }
        );
      })
      .catch((error) => {
        this.$store.commit("appConfig/UPDATE_CSS_VARIABLES", cssDefault);
        this.$store.commit("brand/SET_BRAND_NAME", "ChocoCRM", { root: true });
      })
      .finally(() => {
        const cssVariables = store.getters["appConfig/cssVariables"];
        const bodyStyles = document.body.style;
        bodyStyles.setProperty("--primary", "#0060AB");
        for (const [key, value] of Object.entries(cssVariables)) {
          bodyStyles.setProperty(key, value);
        }
      });

    //get menu list
    const { fetchGetMenuList } = menuModel();
    fetchGetMenuList.payload.Brand_Ref = await this.$store.dispatch(
      "brand/fetchBrandRef"
    );
    await store
      .dispatch("api/apiRequest", fetchGetMenuList)
      .then((response) => {
        this.$store.commit("verticalMenu/UPDATE_MENU_LIST", response.data);
      })
      .catch((error) => {})
      .finally(() => {});

    //clear loading
    this.$store.commit("appConfig/UPDATE_CONFIG", true);
    const appLoading = document.getElementById("loading-bg");
    if (appLoading) {
      appLoading.style.display = "none";
    }
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;
    const vm = getCurrentInstance().proxy;
    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    // Add query data in every path
    watch(
      () => vm.$route,
      () => {
        store.commit("appConfig/UPDATE_BREADCUMB");
        if (sessionStorage.getItem(window.brandSessionStorage)) {
          vm.$router
            .replace({
              path: vm.$route.fullPath,
              query: {
                brandRef: sessionStorage.getItem(window.brandSessionStorage),
              },
            })
            .catch(() => {});
        }
      }
    );

    return {
      skinClasses,
      enableScrollToTop,
    };
  },
};
</script>
